import { useCallback, useEffect, useRef } from 'react';
import { DashboardHeader } from './components/DashboardHeader'
import { PanelWorkspace } from './components/layout/PanelWorkspace'
import { ThemeProvider } from './state/themeStore'
import { newWebSocketRpcSession } from "capnweb"
import { Bus } from '../shared/lib/bus'
import { api } from './lib/api'
import { getDashboardApiPath, getDashboardWebsocketPath } from './lib/appSlug'
import { useStatusStore } from './state/statusStore'
import { useSelectionStore } from './state/selectionStore'
import { AgentState } from '../server/agent'
import { useAuthStore } from './state/authStore'
import { AuthScreen } from './components/AuthScreen'

export const bus = new Bus();
export const firehose = new Bus();

declare global {
  interface Window {
    api?: ReturnType<typeof newWebSocketRpcSession>
    subscribeAgent?: (agentId: string) => boolean
    unsubscribeAgent?: (agentId: string) => boolean
  }
}

function DashboardContent() {
  const socketRef = useRef<WebSocket | null>(null)
  const reconnectTimerRef = useRef<number | null>(null)
  const activeAgentRef = useRef<string | null>(null)
  const desiredAgentRef = useRef<string | null>(null)
  const subscribedAgentsRef = useRef<Set<string>>(new Set())
  const manualSubscriptionsRef = useRef<Set<string>>(new Set())
  const setStatus = useStatusStore((state) => state.set)

  const sendAgentSocketMessage = useCallback(
    (type: 'agentSubscribe' | 'agentUnsubscribe', agentId: string) => {
      if (!agentId) {
        return false
      }
      const socket = socketRef.current
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        return false
      }
      socket.send(JSON.stringify({ type, agent_id: agentId }))
      return true
    },
    [],
  )

  const syncSubscriptions = useCallback(() => {
    const desired = new Set<string>(manualSubscriptionsRef.current)
    if (desiredAgentRef.current) {
      desired.add(desiredAgentRef.current)
    }
    const active = subscribedAgentsRef.current

    for (const agentId of Array.from(active)) {
      if (!desired.has(agentId)) {
        if (sendAgentSocketMessage('agentUnsubscribe', agentId)) {
          active.delete(agentId)
        }
      }
    }

    for (const agentId of desired) {
      if (!active.has(agentId)) {
        if (sendAgentSocketMessage('agentSubscribe', agentId)) {
          active.add(agentId)
        }
      }
    }
  }, [sendAgentSocketMessage])

  const updateActiveAgent = (agentId: string | null) => {
    activeAgentRef.current = agentId
    desiredAgentRef.current = agentId
    syncSubscriptions()
  }

  useEffect(() => {
    // Add radial sheen effect handler for panel-controls buttons
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const button = target.closest('.panel-controls .btn');
      if (button && button instanceof HTMLElement) {
        button.classList.add('sheen-active');
        setTimeout(() => {
          button.classList.remove('sheen-active');
        }, 200); // Match animation duration
      }
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);

  useEffect(() => {
    window.subscribeAgent = (agentId: string) => {
      if (!agentId) {
        return false
      }
      manualSubscriptionsRef.current.add(agentId)
      syncSubscriptions()
      return true
    }
    window.unsubscribeAgent = (agentId: string) => {
      if (!agentId) {
        return false
      }
      manualSubscriptionsRef.current.delete(agentId)
      syncSubscriptions()
      return true
    }
    return () => {
      delete window.subscribeAgent
      delete window.unsubscribeAgent
    }
  }, [syncSubscriptions])

  useEffect(() => {
    let active = true
    let attempt = 0

    const setConnectionStatus = (connected: boolean) => {
      setStatus({ websocketConnected: connected })
    }

    const handleAgentEvent = (eventName: string | undefined) => {
      switch (eventName) {
        case 'llm/run':
          setStatus({ agentState: 'running' })
          break
        case 'llm/end':
        case 'llm/cancel':
        case 'llm/paused':
          setStatus({ agentState: 'idle' })
          break
        case 'llm/error':
          setStatus({ agentState: 'error' })
          break
        default:
          break
      }
    }

    const isAgentState = (value: unknown): value is AgentState =>
      value === 'running' || value === 'idle' || value === 'error'

    const connect = () => {
      if (!active) return
      const base = window.location.origin.replace(/^http/, 'ws')
      const socket = new WebSocket(`${base}${getDashboardWebsocketPath()}`)
      socketRef.current = socket

      socket.onopen = () => {
        attempt = 0
        console.log('[dashboard] websocket connected')
        setConnectionStatus(true)
        syncSubscriptions()
      }

      socket.onmessage = event => {
        try {
          const data = JSON.parse(event.data)
          if(data.event !== 'llm/delta/json' && data.event !== 'llm/delta/text') {
            console.log('[dashboard] websocket message', data)
          }

          const messageAgentId = typeof data.agent_id === 'string' ? data.agent_id : null

          if (typeof data.event === 'string') {
            firehose.emit(data.event, { agentId: messageAgentId, payload: data.payload })
          }

          const activeAgentId = activeAgentRef.current
          if (messageAgentId && activeAgentId && messageAgentId !== activeAgentId) {
            return
          }

          // Emit event through bus for components to handle
          if (typeof data.event === 'string') {
            bus.emit(data.event, data.payload)

            if (data.event === 'meta/set' && data.payload?.key === 'active_agent_id') {
              const nextId = typeof data.payload?.value === 'string' ? data.payload.value : null
              updateActiveAgent(nextId)
            }

            // Handle agent state
            handleAgentEvent(data.event)
          }
        } catch (err) {
          console.warn('[dashboard] failed to parse websocket message', err)
        }
      }

      const scheduleReconnect = () => {
        if (!active) return
        const delay = Math.min(1000 * 2 ** attempt, 10000)
        reconnectTimerRef.current = window.setTimeout(connect, delay)
        attempt += 1
      }

      socket.onerror = err => {
        console.error('[dashboard] websocket error', err)
        socket.close()
        setConnectionStatus(false)
      }

      socket.onclose = event => {
        console.log('[dashboard] websocket closed', event.code, event.reason)
        setConnectionStatus(false)
        subscribedAgentsRef.current.clear()
        scheduleReconnect()
      }
    }

    connect()

    void (async () => {
      try {
        const status = await api().agent.status()
        if (status && typeof status === 'object' && 'state' in status) {
          const maybeState = (status as { state?: unknown }).state
          if (isAgentState(maybeState)) {
            setStatus({ agentState: maybeState })
          }
        }
      } catch (error) {
        console.warn('[dashboard] failed to load initial agent status', error)
      }

      try {
        const agentId = await api().services.meta.get('active_agent_id')
        updateActiveAgent(typeof agentId === 'string' ? agentId : null)
      } catch (error) {
        console.warn('[dashboard] failed to resolve active agent id', error)
      }
    })()

    return () => {
      active = false
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current)
      }
      socketRef.current?.close()
      setConnectionStatus(false)
    }
  }, [setStatus, syncSubscriptions])

  return (
    <div className="app-container">
      <DashboardHeader />
      <main className="main-workspace">
        <div className="workspace-inner">
          <PanelWorkspace />
        </div>
      </main>
    </div>
  )
}

export default function App() {
  const apiRef = useRef<ReturnType<typeof newWebSocketRpcSession> | null>(null)
  const select = useSelectionStore((state) => state.set)
  const authStatus = useAuthStore((state) => state.status)
  const setAuth = useAuthStore((state) => state.set)

  useEffect(() => {
    let active = true
    const loadAuth = async () => {
      setAuth({ status: 'loading', error: null })
      try {
        const response = await fetch('/auth/me')
        if (!active) return
        if (response.ok) {
          const data = await response.json().catch(() => ({}))
          if (data?.user) {
            setAuth({ status: 'authenticated', user: data.user, error: null })
            return
          }
        }
        setAuth({ status: 'unauthenticated', user: null })
      } catch (error) {
        if (!active) return
        setAuth({ status: 'unauthenticated', user: null, error: 'Failed to load session' })
      }
    }

    loadAuth()

    return () => {
      active = false
    }
  }, [setAuth])

  useEffect(() => {
    if (authStatus !== 'authenticated') {
      if (apiRef.current) {
        const stub = apiRef.current
        delete window.api
        apiRef.current = null
        const disposer = (stub as unknown as { [key: symbol]: unknown })[Symbol.dispose]
        if (typeof disposer === 'function') {
          disposer.call(stub)
        }
      }
      return
    }

    const stub = newWebSocketRpcSession(getDashboardApiPath())
    apiRef.current = stub
    // NOTE: window.api is for developer debugging use only in console
    // All components MUST use the api() helper from lib/api.ts
    // The WebSocket RPC API (window.api) is NOT to be used directly in components
    window.api = stub

    // Prime selection with the most recent agent message
    const loadChatHistory = async () => {
      try {
        const agentId = await api().services.meta.get('active_agent_id')
        const agent = await api().services.agent.get(agentId)
        const lastMessageId = agent?.last_message_id
        if (lastMessageId && typeof lastMessageId === 'number') {
          try {
            const message = await api().services.context.getMessage(lastMessageId)
            if (message) {
              select({
                session_id: message.session_id,
                message_id: message.id,
              })
              return
            }
          } catch (error) {
            console.warn('[chat] failed to load message for last agent message id:', error)
          }
          select({ message_id: lastMessageId })
        }
      } catch (error) {
        console.error('[chat] failed to trigger chat history load:', error)
      }
    }

    loadChatHistory()

    return () => {
      delete window.api
      apiRef.current = null
      const disposer = (stub as unknown as { [key: symbol]: unknown })[Symbol.dispose]
      if (typeof disposer === 'function') {
        disposer.call(stub)
      }
    }
  }, [authStatus, select])

  return (
    <ThemeProvider>
      {authStatus === 'authenticated' ? <DashboardContent /> : <AuthScreen />}
    </ThemeProvider>
  );
}
